import { useState } from 'react'
import { useTheme } from '@emotion/react'
import { observer, inject } from 'mobx-react'
import dayjs from 'dayjs'

import { ActionMenu, SVGWrapper } from '../../../components'
import { planTypeIdToAccountKey } from '../../../constants'
import { PiggyBank, Link } from '../../../assets/icons'
import { numberToDollars, withRouterProps } from '../../../utils'
import { getParticipantName, refreshPlaidLink } from '../utils'
import {
  SectionBalance,
  DollarSign,
  SectionRow,
  SectionLabel,
  AccountNameLink,
  NameMarker,
  MobileLinkIcon,
  SectionSubLabel,
  NameField,
  LinkedAccountMeta,
  BalanceField,
} from './DashboardStyle'
import DashboardDeleteAccount from './DashboardDeleteAccount'

const typeToPathKey = {
  Annuity: 'annuity',
  Institutional: 'institutional',
  NonGc: 'non-gc',
  Pension: 'pension',
}

const DashboardAccount = props => {
  const {
    features,
    account: { managedAccount },
    findPlaidAccountById,
    accountInsight: { sortedContributionList },
  } = props.store
  const {
    name,
    balance,
    type,
    id,
    participantId,
    contributing,
    employerSponsoredAccount,
    plaidLinked,
  } = props.account
  const { showLumpSumNotice } = props
  const theme = useTheme()
  const [deleteStatus, setDeleteStatus] = useState('done')
  const [deleteActive, setDeleteActive] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const plaidAccount = findPlaidAccountById(Number(id))
  const plaidAccountLastRefreshDate = plaidAccount?.lastRefreshedOn ?? null
  const hasMonthlyContribution = sortedContributionList.some(item => item.accountId === id)

  const toggleDeleteModal = () => {
    setDeleteStatus('done')
    setDeleteActive(isActive => !isActive)
  }

  const handleEdit = () => {
    const { navigate, account } = props
    const { type, id, planType } = account
    const accountType = planTypeIdToAccountKey[planType]

    navigate(`/accounts/${typeToPathKey[type]}/${accountType}/${id}`)
  }

  const handleDeleteAccount = async () => {
    const { deleteAccount, getAllPensions } = props.store
    setDeleteStatus('loading')

    try {
      await deleteAccount(type, id)
      await getAllPensions()
    } catch (err) {
      console.error(err)
      setDeleteStatus('error')
    }
  }

  const handlePlaidRefresh = async () => {
    const { store } = props
    setIsRefreshing(true)

    // Handle errors here so other `refreshPlaidLink` callers keep their existing behavior.
    try {
      await refreshPlaidLink(store, id)
      await Promise.all([store.getExistingPlaidLinks(), store.getAccounts()])
    } catch (err) {
      console.error(err)
      store.showToast({
        severity: 'error',
        message: 'We could not refresh this account. Please try again.',
      })
    } finally {
      setIsRefreshing(false)
    }
  }

  const menuItems = [
    { label: 'Edit', onClick: handleEdit },
    ...(plaidLinked ? [{ label: 'Refresh', onClick: handlePlaidRefresh }] : []),
    ...(employerSponsoredAccount
      ? []
      : [{ label: 'Remove', onClick: toggleDeleteModal, danger: true }]),
  ]

  const nameMarker =
    (type === 'Institutional' && features.managedAccountAvailable && managedAccount && '*') ||
    (type === 'Pension' && showLumpSumNotice && '**') ||
    ''

  return (
    <SectionRow id={props.id}>
      <NameField>
        <SectionLabel>
          <AccountNameLink type='button' onClick={handleEdit}>
            {name}
          </AccountNameLink>
          {nameMarker && <NameMarker>{nameMarker}</NameMarker>}
          {plaidLinked && (
            <MobileLinkIcon>
              <Link width='16' height='16' />
            </MobileLinkIcon>
          )}
        </SectionLabel>
        <SectionSubLabel>{getParticipantName(props.store, participantId)}</SectionSubLabel>
        {plaidLinked && (
          <LinkedAccountMeta>
            <Link />
            Linked account last refreshed{' '}
            {plaidAccountLastRefreshDate
              ? `${dayjs(plaidAccountLastRefreshDate).format('MM/DD/YYYY')} (${dayjs(
                  plaidAccountLastRefreshDate
                ).fromNow()})`
              : 'Never'}
          </LinkedAccountMeta>
        )}
      </NameField>
      <BalanceField>
        {contributing && hasMonthlyContribution && (
          <SVGWrapper
            svg={PiggyBank}
            fill={theme.lightestGray}
            hoverFill={theme.lightestGray}
            size='medium'
            cursor='default'
          />
        )}
        <SectionBalance>
          <DollarSign>$</DollarSign>
          {numberToDollars(balance, true)}
        </SectionBalance>
        <ActionMenu
          items={menuItems}
          ariaLabel={`Actions for ${name}`}
          busy={isRefreshing}
          busyLabel={`Refreshing ${name}`}
        />
      </BalanceField>
      <DashboardDeleteAccount
        handleConfirmation={handleDeleteAccount}
        isModalOpen={deleteActive}
        toggleModal={toggleDeleteModal}
        loading={deleteStatus === 'loading'}
        error={deleteStatus === 'error'}
      />
    </SectionRow>
  )
}

export default withRouterProps(inject('store')(observer(DashboardAccount)))
